import React from "react";
import type { DepartmentOverviewRow } from "../../services/dashboard.service";
import SectionCard from "./SectionCard";

// Map well-known specialisations to the exact Figma colours; fall back to a
// stable per-index cycle for unknown specialisations so the same name always
// gets the same dot colour across renders.
const KNOWN_DOT: Record<string, string> = {
  cardiology:  "bg-red-500",
  neurology:   "bg-violet-500",
  orthopedics: "bg-blue-500",
  pediatrics:  "bg-green-500",
  general:     "bg-cyan-500",
};
const FALLBACK_DOT = ["bg-red-500", "bg-violet-500", "bg-blue-500", "bg-green-500", "bg-cyan-500", "bg-orange-500", "bg-fuchsia-500"];

function dotColor(spec: string, fallbackIndex: number): string {
  return KNOWN_DOT[spec.trim().toLowerCase()] ?? FALLBACK_DOT[fallbackIndex % FALLBACK_DOT.length];
}

interface Props {
  rows: DepartmentOverviewRow[];
  onViewAll?: () => void;
}

export const DepartmentOverview: React.FC<Props> = ({ rows, onViewAll }) => {
  // Doctor headcount per department, not patient/appointment activity — a
  // department with a real doctor but zero patients this month must still
  // show that doctor, not a misleading "0".
  const max = Math.max(1, ...rows.map((r) => r.doctor_count));

  return (
    <SectionCard
      title="Department Overview"
      action={
        onViewAll && (
          <button
            onClick={onViewAll}
            className="text-xs font-medium text-slate-500 hover:text-slate-800 flex items-center gap-1"
          >
            View All <span aria-hidden>›</span>
          </button>
        )
      }
    >
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center">No active doctors yet.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r, i) => (
            <li key={r.department}>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-700">
                  <span className={`h-2 w-2 rounded-full ${dotColor(r.department || "", i)}`} />
                  {r.department || "Unspecified"}
                </span>
                <span className="font-semibold text-slate-900">
                  {r.doctor_count} {r.doctor_count === 1 ? "doctor" : "doctors"}
                </span>
              </div>
              <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-slate-900 rounded-full"
                  style={{ width: `${(r.doctor_count / max) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
};

export default DepartmentOverview;
